import React from "react";
import { Helmet } from "react-helmet-async";
import { useI18n } from "@/i18n/I18nContext";
import { Reveal } from "@/components/Reveal";
import { Star } from "lucide-react";
import { getReviews, FLAG, SOURCE } from "@/data/reviews";

const SourceBadge = ({ source }) => {
  const s = SOURCE[source];
  if (!s) return null;
  return (
    <span data-testid={`review-source-${source}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: s.color }}>
      {s.label}
    </span>
  );
};

export default function ReviewsPage() {
  const { t, lang } = useI18n();
  const { reviews, average, count } = getReviews(lang);

  const reviewSchema = {
    "@context": "https://schema.org",
    "@type": "BedAndBreakfast",
    "name": "I Lake It Bellagio",
    "url": "https://www.ilakeitbellagio.it",
    "image": "https://www.ilakeitbellagio.it/og-image.jpg",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Bellagio",
      "addressRegion": "CO",
      "postalCode": "22021",
      "addressCountry": "IT",
    },
    "geo": { "@type": "GeoCoordinates", "latitude": 45.9703849, "longitude": 9.243819 },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": average,
      "bestRating": 5,
      "worstRating": 1,
      "reviewCount": count,
    },
    "review": reviews.map((r) => ({
      "@type": "Review",
      "author": { "@type": "Person", "name": r.name },
      "reviewRating": { "@type": "Rating", "ratingValue": r.rating, "bestRating": 5, "worstRating": 1 },
      "reviewBody": r.text,
      "datePublished": r.date,
    })),
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(reviewSchema)}</script>
      </Helmet>
      <section className="pt-36 pb-12 bg-[#F2EFEB]">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Reveal>
            <h1 className="font-display text-5xl md:text-6xl text-[#1A1A1A] tracking-tight">{t("reviews.title")}</h1>
            <p className="mt-5 text-lg text-[#5C5C5C] max-w-2xl">{t("reviews.subtitle")}</p>
            <div className="mt-8 flex items-center gap-6 flex-wrap">
              <div className="flex items-baseline gap-2">
                <span className="font-display text-5xl text-[#1A1A1A]" data-testid="reviews-average">{average}</span>
                <span className="text-[#5C5C5C]">/ 5</span>
              </div>
              <div>
                <div className="flex gap-0.5 mb-1">
                  {[1, 2, 3, 4, 5].map(n => <Star key={n} className="w-5 h-5 fill-[#E6B800] stroke-[#E6B800]" />)}
                </div>
                <span className="text-sm text-[#5C5C5C]">{t("reviews.basedOn")} {count} {t("reviews.averageLabel").toLowerCase()}</span>
              </div>
              <div className="flex items-center gap-2">
                <SourceBadge source="google" />
                <SourceBadge source="booking" />
                <SourceBadge source="airbnb" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-6">
            {reviews.map((r, i) => (
              <Reveal key={r.id} delay={(i % 6) * 50}>
                <article data-testid={`review-${r.id}`} className="bg-white p-8 rounded-2xl border border-black/5 h-full flex flex-col">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map(n => <Star key={n} className={`w-4 h-4 ${n <= r.rating ? "fill-[#E6B800] stroke-[#E6B800]" : "stroke-[#ddd]"}`} />)}
                    </div>
                    <SourceBadge source={r.source} />
                  </div>
                  <h3 className="font-display text-xl text-[#1A1A1A] mb-3">{r.title}</h3>
                  <p className="text-[#5C5C5C] leading-relaxed mb-5 flex-1">"{r.text}"</p>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-[#1A1A1A]">{FLAG[r.country] || "🌍"} {r.name}</span>
                    <span className="text-[#5C5C5C]">{r.score}</span>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
